<script setup>
import { cn } from '@/lib/utils'
import { computed, inject, onMounted, ref, watch } from 'vue'

/**
 * `src` is not required: most callers pass a nullable `avatar_url` straight
 * through, and a user who has never uploaded a picture has null there.
 */
const props = defineProps({
  class: { type: String, default: '' },
  src: { type: String, default: null },
  alt: { type: String, default: '' },
})

const imageLoadingStatus = inject('imageLoadingStatus', ref('idle'))
const hasLoaded = ref(false)

const hasSource = computed(() => Boolean(props.src))

const handleLoad = () => {
  hasLoaded.value = true
  imageLoadingStatus.value = 'loaded'
}

const handleError = () => {
  hasLoaded.value = false
  imageLoadingStatus.value = 'error'
}

/**
 * With no source there is nothing to wait for, so go straight to 'error' and let
 * the fallback take over. Announcing 'loading' regardless was what put a broken
 * image where a person's face should be: an `<img>` with no `src` renders the
 * browser's broken-image glyph and never fires `error`, so the fallback it was
 * meant to defer to was never reached.
 */
const sync = () => {
  hasLoaded.value = false
  imageLoadingStatus.value = hasSource.value ? 'loading' : 'error'
}

onMounted(sync)

watch(() => props.src, sync)
</script>

<template>
  <img
    v-if="hasSource && (imageLoadingStatus === 'loading' || imageLoadingStatus === 'loaded')"
    :src="src"
    :alt="alt"
    :class="cn('aspect-square h-full w-full', props.class)"
    @load="handleLoad"
    @error="handleError"
  />
</template>
